<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="scrollElement" :class="containerStyles">
    <div class="col-start-2 col-span-12 h-3 bg-orange mt-20 lg:mt-34" />
    <header2
      :text="title"
      :is-umbraco="isUmbraco"
      :theme="themeType"
      classes="col-start-2 col-span-12 mt-6 mb-10 lg:mt-10 lg:col-start-2 lg:col-span-4"
    />
    <div
      class="col-start-2 col-span-12 lg:mt-10 lg:col-start-6 lg:col-span-8 grid auto-rows-min grid-cols-12 md:grid-cols-8 gap-x-5 md:gap-x-12 2xl:gap-x-20 gap-y-16"
    >
      <contact-module
        v-for="contact in contacts"
        :key="contact.fullName"
        :full-name="contact.fullName"
        :job-position="contact.jobPosition"
        :email="contact.email"
        :phone="contact.phone"
        :link="contact.link"
        :link-title="contact.linkTitle"
        :country="contact.country"
        :image="contact.image"
        :imageAltText="contact.imageAltText"
        :theme-type="themeType"
        classes="col-span-full md:col-span-4"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import {
  toRefs,
  computed,
  Ref,
  ref,
  onBeforeMount,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import { IContactListProps, IContactProps } from "@/types/components/IContactListProps";
import { changeTheme, getThemeType } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import Header2 from "@components/Typography/Header2.ce.vue";
import ContactModule from "@atoms/Contact/ContactModule.ce.vue";

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);
const isLoading = ref(true);

const props = withDefaults(defineProps<IContactListProps>(), {
  isUmbraco: false,
});
const { title, contactList, theme, isUmbraco } = toRefs(props);

const onEnter = () => {
  changeTheme(theme.value);
};
const onExit = () => {};

onBeforeMount(() => {
  if (theme.value) {
    isLoading.value = false;
  }
});

onMounted(() => {
  if (!isLoading.value) {
    observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
      threshold: 0.3,
    });
  }
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} sectionContainer place-content-start md:min-h-full`;
});

const themeType = computed(() => {
  return getThemeType(theme.value);
});

const contacts = computed(() => {
  const constactsArray: IContactProps[] = JSON.parse(contactList.value);
  const contacts = constactsArray.map((contact) => {
    return {
      fullName: contact.fullName,
      jobPosition: contact.jobPosition,
      email: contact.email,
      phone: contact.phone,
      link: contact.link,
      linkTitle: contact.linkTitle,
      country: contact.country,
      image: contact.image != "" ? contact.image : null,
      imageAltText: contact.imageAltText,
    };
  });
  return contacts;
});
</script>
